import { DateTime } from 'luxon';
import { RefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useFlags } from '../../../../context/featureFlag';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/context-menu/context-menu';
import { cn } from '../../../../components/utils';
import { useViewer } from '../../../../context/viewer';
import { usePlanContext } from '../../context/PlanContext';
import { comparePhasesForDisplay } from '../../hooks/cross-team-zero';
import { blockDropNewStartDate, blockPendingRenderDates } from '../block-drag';
import {
  getLastTrackRowIndex,
  precededByTrackRow,
  TimelineRow,
} from '../track-rows';
import { TimelineData } from '../timeline-data';
import { AddBlockData, Block, Phase, TrackPreview } from '../types';
import {
  BOTTOM_PADDING,
  DATE_HEADER_HEIGHT,
  getEffectiveTrackColor,
  SIDE_PANEL_WIDTH,
  stylesForBlock,
  stylesForIndex,
  TRACK_HEIGHT,
} from '../utils';
import { BlockItem } from './BlockItem';
import { AnnualPlannerUnavailableState } from './AnnualPlannerUnavailableState';
import { HoverButton } from './HoverButton';
import { PhaseItem } from './PhaseItem';
import { TimelineHeader } from './TimelineHeader';

// Cross-team row heights mirror the sidebar `AdditionalTeamsTracks` so the
// two panels stay vertically aligned row-for-row. Keep these in sync if the
// sidebar component changes its heights.
const CROSS_TEAM_HEADER_HEIGHT = 48; // "Additional Teams (N)" outer header
const CROSS_TEAM_TEAM_HEADER_HEIGHT = 48; // per-team name header
const CROSS_TEAM_PHASES_SPACER_HEIGHT = 48; // per-team "Phases" sub-row
const CROSS_TEAM_PARENT_ROW_HEIGHT = 48;
const CROSS_TEAM_SUBTRACK_ROW_HEIGHT = 40;
// Bar height matches the current-team block/phase visual (`stylesForBlock` =
// 34px) so cross-team items don't look thicker than the rest of the timeline.
const CROSS_TEAM_BLOCK_HEIGHT = 34;
const CROSS_TEAM_PHASE_HEIGHT = 34;
const CROSS_TEAM_PHASE_TOP =
  (CROSS_TEAM_PHASES_SPACER_HEIGHT - CROSS_TEAM_PHASE_HEIGHT) / 2;

type BlockPanelProps = {
  navWidth: number;
  timeline: TimelineData;
  scrollPanelRef: RefObject<HTMLDivElement>;
  blockPanelRef: RefObject<HTMLDivElement>;
  teamId: string;
  phases: Phase[];
  tracks: TrackPreview[];
  rows: TimelineRow<TrackPreview>[];
  blocks: Block[];
  blockToCopy: Block | undefined;
  setAddBlockData: (data: AddBlockData) => void;
  handleBlockPanelScroll: React.UIEventHandler<HTMLDivElement>;
  setNewPhaseStartDate: (startDate: DateTime) => void;
  setPhaseIdToEdit: (phaseId: string) => void;
  onDeletePhase: (phaseId: string) => void;
  setBlockIdToEdit: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onCancelBlock: (blockId: string) => void;
  setBlockToCopy: (block: Block) => void;
  selectedAthleteId?: string;
  onMonthClick?: (date: DateTime) => void;
  /** A drag drop proposed a move; the confirm modal decides (Phase 04). */
  onDragMove?: (block: Block, newStartDate: string) => void;
  /** The drag move being decided or saved, for the pending render. */
  pendingMove?: { blockId: string; startDate: string; saving: boolean } | null;
};

/**
 * The drop target for block drags: the timeline's row area. One droppable for
 * the whole area is enough — the drop only decides the new start date, so the
 * day offset comes from the drag distance, and the block always stays on its
 * own track.
 */
function TimelineDropArea({ children }: { children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({ id: 'timeline-rows' });
  return (
    <div ref={setNodeRef} className="relative">
      {children}
    </div>
  );
}

/**
 * Mounts the block-drag DndContext around the timeline rows when block drags
 * are enabled, and renders the rows untouched otherwise (useDraggable
 * requires a DndContext, so the draggable blocks only mount under it).
 */
function TimelineDragArea({
  enabled,
  sensors,
  onDragEnd,
  children,
}: {
  enabled: boolean;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  children: React.ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
    >
      <TimelineDropArea>{children}</TimelineDropArea>
    </DndContext>
  );
}

export function BlockPanel({
  blockPanelRef,
  navWidth,
  phases,
  timeline,
  scrollPanelRef,
  teamId,
  tracks,
  rows,
  blocks,
  blockToCopy,
  setAddBlockData,
  handleBlockPanelScroll,
  setNewPhaseStartDate,
  setPhaseIdToEdit,
  onDeletePhase,
  setBlockIdToEdit,
  onDeleteBlock,
  onCancelBlock,
  setBlockToCopy,
  selectedAthleteId,
  onMonthClick,
  onDragMove,
  pendingMove,
}: BlockPanelProps) {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const { dragInteractionsFlag } = useFlags();
  // Blocks View revoked means the blocks sync group is suppressed server-side,
  // so the rows would render as bare track skeletons — the Figma empty state
  // replaces them instead. The date header stays: phases are governed by
  // Phases View, independently of Blocks View.
  const canViewBlocks = hasPermissions('plan.blocks', 'view');
  // Phases View revoked means no phase data (suppressed in usePlanData) —
  // the phase row and the cross-team per-team Phases spacer are removed
  // entirely. The sidebar (TrackPanel/AdditionalTeamsTracks) gates its
  // Phases label row on the same permission so the panels stay aligned.
  const canViewPhases = hasPermissions('plan.phases', 'view');
  const {
    hasAdditionalTeams,
    additionalTeamsData,
    collapsedCrossTeamTeamIds,
    collapsedCrossTeamTrackIds,
  } = usePlanContext();
  const [addDateHover, setAddDateHover] = useState<{
    trackId: string;
    offset: number;
  } | null>(null);
  const [copyDate, setCopyDate] = useState<DateTime<true> | null>(null);
  const { today, totalWeeks, timelineStart, dateHeaderScale } = timeline;
  const tracksById = useMemo(
    () => new Map(tracks.map((t) => [t.id, t])),
    [tracks],
  );

  // Index of the row that owns the list's closing bottom border — see
  // getLastTrackRowIndex for why this must be the row genuinely last
  // overall, not merely the last track-kind row.
  const lastTrackIndex = getLastTrackRowIndex(rows);

  // Block drag (Phase 04): the flag turns the interaction on; Blocks Edit
  // decides whether the DnD context mounts at all — without edit permission
  // no block offers a drag affordance, so the context is not needed.
  const canEditBlocks = hasPermissions('plan.blocks', 'edit');
  const dragEnabled = dragInteractionsFlag && !!onDragMove && canEditBlocks;
  // A small activation distance keeps a plain click a navigation: the link
  // under the pointer still opens the block page.
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onDragMove) return;
    const block = event.active.data.current?.['block'] as Block | undefined;
    const newStartDate = blockDropNewStartDate(
      block,
      event.over,
      event.delta.x,
      dateHeaderScale,
    );
    // A drop on (or a sub-pixel jitter around) the current start date, or
    // outside the rows, is a no-op: no modal, no toast.
    if (!newStartDate || !block) return;
    // The block is a link: the browser fires click on pointerup after a drag,
    // which would navigate to the block page and unmount the confirm modal.
    // Swallow that one click (it arrives on this same task; the cleanup
    // timeout covers the no-click case).
    const swallowNextClick = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('click', swallowNextClick, {
      capture: true,
      once: true,
    });
    window.setTimeout(() => {
      document.removeEventListener('click', swallowNextClick, {
        capture: true,
      });
    }, 0);
    onDragMove(block, newStartDate);
  };

  // While a drag move is being decided or saved, the dragged block renders at
  // its proposed position as pending — both dates shifted, so the bar keeps
  // the block's duration; a cancel reverts by removing the override, a save
  // commits it through the normal block edit.
  const blocksForRows = pendingMove
    ? blocks.map((b) =>
        b.id === pendingMove.blockId
          ? { ...b, ...blockPendingRenderDates(b, pendingMove.startDate) }
          : b,
      )
    : blocks;

  return (
    <div
      ref={scrollPanelRef}
      className="flex flex-grow overflow-x-auto h-full"
      tabIndex={0}
      style={{
        width: `calc(100vw - ${navWidth + SIDE_PANEL_WIDTH}px)`,
      }}
    >
      <div
        className="h-full relative bg-gray-50 flex flex-col flex-grow"
        style={{
          minWidth: totalWeeks * 7 * dateHeaderScale,
        }}
      >
        <div
          className="border-b top-0 left-0 w-full bg-white sticky flex-shrink-0"
          style={{
            height: DATE_HEADER_HEIGHT,
          }}
        >
          <TimelineHeader
            timeline={timeline}
            dateHeaderScale={dateHeaderScale}
            onMonthClick={onMonthClick}
          />
        </div>

        {/** Phase row — only when the viewer can see phases */}
        {canViewPhases && (
          <div
            style={{
              height: TRACK_HEIGHT,
              top: DATE_HEADER_HEIGHT,
            }}
            className="flex w-full hover:bg-gray-50 bg-gray-50 border-b sticky flex-shrink-0"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const offset = e.clientX - rect.left;
              const roundedOffset = offset - (offset % dateHeaderScale);
              setAddDateHover({
                trackId: 'phase',
                offset: roundedOffset,
              });
            }}
            onMouseLeave={() => setAddDateHover(null)}
          >
            {addDateHover?.trackId === 'phase' && (
              <HoverButton
                dateHeaderScale={dateHeaderScale}
                timelineStart={timelineStart}
                offset={addDateHover.offset}
                ariaLabel={t('addPhase')}
                disabledReason={
                  hasPermissions('plan.phases', 'create')
                    ? undefined
                    : t('permissionDisabled.create', { item: t('phases') })
                }
                onClick={(startDate) => {
                  setNewPhaseStartDate(startDate);
                  setAddDateHover(null);
                }}
              />
            )}
            {phases.map((p) => (
              <PhaseItem
                dateHeaderScale={dateHeaderScale}
                key={p.id}
                phase={p}
                timelineStart={timelineStart}
                hideHoverButton={() => setAddDateHover(null)}
                setPhaseIdToEdit={setPhaseIdToEdit}
                deletePhase={onDeletePhase}
              />
            ))}
          </div>
        )}

        <div
          className="flex-1 overflow-y-auto relative"
          style={{
            paddingBottom: BOTTOM_PADDING,
          }}
          ref={blockPanelRef}
          onScroll={handleBlockPanelScroll}
        >
          {canViewBlocks ? (
            <TimelineDragArea
              enabled={dragEnabled}
              sensors={dragSensors}
              onDragEnd={handleDragEnd}
            >
              {rows.map((row) => {
                if (row.kind === 'archived-header') {
                  // Empty spacer aligning the timeline to the sidebar's "Archived
                  // Tracks (N)" header row. Only the top border is drawn, closing
                  // the active section above; no border-b, mirroring the sidebar
                  // header (no divider between it and the first archived row).
                  return (
                    <div
                      key="archived-header"
                      className="w-full border-t-4"
                      style={{ height: TRACK_HEIGHT }}
                      aria-hidden="true"
                    />
                  );
                }

                // The group label owns no blocks: it stands in for the row of a
                // still-active parent, whose blocks are already drawn on its
                // active row above. The spacer is still needed so this panel
                // stays row-aligned with the sidebar's label strip.
                if (row.kind === 'group-label') {
                  return (
                    <div
                      key={`group-label-${row.parentTrackId}`}
                      // Mirrors the sidebar's label row, border-b included: a
                      // collapsed label takes over the section's closing border.
                      className={cn(
                        'w-full',
                        row.index === lastTrackIndex && 'border-b',
                      )}
                      style={{ height: TRACK_HEIGHT }}
                      aria-hidden="true"
                    />
                  );
                }

                // The Additional Teams section renders below via
                // hasAdditionalTeams; nothing to draw for this marker here.
                if (row.kind === 'additional-teams') return null;

                const { track, index, isArchived, isSubTrack } = row;
                const rowClassName = cn(
                  'flex w-full',
                  // Archived rows are read-only on the timeline, so no add-block
                  // hover affordance.
                  !isArchived && 'hover:bg-gray-50',
                  index === lastTrackIndex && 'border-b',
                  !isSubTrack && precededByTrackRow(rows, index) && 'border-t',
                );
                const trackBlocks = blocksForRows?.filter(
                  (tb) => tb.trackId === track.id,
                );

                if (isArchived) {
                  return (
                    <div
                      key={track.id}
                      style={stylesForIndex(index)}
                      className={rowClassName}
                    >
                      {/* No effectiveColor here: track-colors support for archived
                      rows is out of scope for this phase. */}
                      {trackBlocks?.map((tb) => (
                        <BlockItem
                          key={tb.id}
                          readOnly
                          block={tb}
                          teamId={teamId}
                          style={stylesForBlock(index)}
                          dateHeaderScale={dateHeaderScale}
                          timelineStart={timelineStart}
                          selectedAthleteId={selectedAthleteId}
                        />
                      ))}
                    </div>
                  );
                }

                return (
                  <div
                    key={track.id}
                    style={stylesForIndex(index)}
                    className={rowClassName}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const offset = e.clientX - rect.left;
                      const roundedOffset = offset - (offset % dateHeaderScale);
                      setAddDateHover({
                        trackId: track.id,
                        offset: roundedOffset,
                      });
                    }}
                    onMouseLeave={() => setAddDateHover(null)}
                  >
                    {addDateHover?.trackId === track.id && (
                      <ContextMenu modal={false}>
                        <ContextMenuTrigger>
                          <HoverButton
                            dateHeaderScale={dateHeaderScale}
                            timelineStart={timelineStart}
                            offset={addDateHover?.offset ?? 0}
                            ariaLabel={t('addBlock')}
                            disabledReason={
                              hasPermissions('plan.blocks', 'create')
                                ? undefined
                                : t('permissionDisabled.create', {
                                    item: t('blocks'),
                                  })
                            }
                            onRightClick={() => {
                              // Capture the right clicked date here as it will
                              // otherwise change as the user moves the mouse
                              setCopyDate(
                                timelineStart.plus({
                                  days:
                                    (addDateHover?.offset ?? 0) /
                                    dateHeaderScale,
                                }),
                              );
                            }}
                            onClick={(startDate) => {
                              setAddBlockData({
                                trackId: addDateHover?.trackId ?? '',
                                startDate,
                                isCopy: false,
                              });
                              setAddDateHover(null);
                            }}
                          />
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          <ContextMenuItem
                            disabled={
                              !blockToCopy ||
                              !hasPermissions('plan.blocks', 'create')
                            }
                            onClick={() => {
                              if (!copyDate) return;
                              setAddBlockData({
                                trackId: addDateHover?.trackId ?? '',
                                startDate: copyDate,
                                isCopy: true,
                              });
                              setCopyDate(null);
                            }}
                          >
                            {t('pasteBlock')}
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    )}
                    {trackBlocks?.map((tb) => (
                      <BlockItem
                        key={tb.id}
                        block={tb}
                        teamId={teamId}
                        style={stylesForBlock(index)}
                        dateHeaderScale={dateHeaderScale}
                        timelineStart={timelineStart}
                        hideHoverButton={() => setAddDateHover(null)}
                        setBlockIdToEdit={setBlockIdToEdit}
                        deleteBlock={onDeleteBlock}
                        cancelBlock={onCancelBlock}
                        setBlockToCopy={setBlockToCopy}
                        selectedAthleteId={selectedAthleteId}
                        effectiveColor={getEffectiveTrackColor(
                          track,
                          tracksById,
                        )}
                        dragActive={dragEnabled}
                        dragState={
                          tb.id === pendingMove?.blockId
                            ? pendingMove.saving
                              ? 'saving'
                              : 'pending'
                            : undefined
                        }
                      />
                    ))}
                  </div>
                );
              })}
            </TimelineDragArea>
          ) : (
            // Sticky so the message stays centered in the visible viewport
            // even though the scroll body spans the full timeline width.
            <div
              className="sticky left-0 flex h-full flex-col items-center justify-center"
              style={{
                width: `calc(100vw - ${navWidth + SIDE_PANEL_WIDTH}px)`,
              }}
            >
              <AnnualPlannerUnavailableState
                stateKey="unableToDisplayBlocks"
                variant="inline"
              />
            </div>
          )}

          {hasAdditionalTeams && (
            <>
              {/* Divider closing the section above Additional Teams, mirroring
                  the sidebar AdditionalTeamsTracks row's border-t-4. */}
              <div
                className="w-full border-b border-t-4"
                style={{ height: CROSS_TEAM_HEADER_HEIGHT }}
                aria-hidden="true"
              />
              {additionalTeamsData.map((team) => {
                const teamCollapsed = collapsedCrossTeamTeamIds.has(
                  team.teamId,
                );
                // Order tracks for display: each parent followed inline by
                // its own subtracks, matching the sidebar's row order. One
                // pass to bucket children by parent, then a flatMap over the
                // parents — O(n) instead of double-filtering per parent.
                // Orphan subtracks (no matching parent) are dropped.
                type Track = (typeof team.tracks)[number];
                const parentTracks: Track[] = [];
                const childrenByParentId = new Map<string, Track[]>();
                for (const track of team.tracks) {
                  if (track.parentTrackId === null) {
                    parentTracks.push(track);
                    continue;
                  }
                  const list =
                    childrenByParentId.get(track.parentTrackId) ?? [];
                  list.push(track);
                  childrenByParentId.set(track.parentTrackId, list);
                }
                const orderedTracks = parentTracks.flatMap((parent) => [
                  parent,
                  ...(childrenByParentId.get(parent.id) ?? []),
                ]);
                const visibleTracks = teamCollapsed
                  ? []
                  : orderedTracks.filter(
                      (track) =>
                        // Parent tracks always render; subtracks render only
                        // when their parent is not in the collapsed set,
                        // matching the current-team `collapsedTracks` pattern.
                        !track.parentTrackId ||
                        !collapsedCrossTeamTrackIds.has(track.parentTrackId),
                    );
                // Deterministic stacking order for overlapping phase bands,
                // shared with the Zero transform so both data paths put the
                // same phase on top (neither query guarantees row order).
                const phases = [...team.phases].sort(comparePhasesForDisplay);
                return (
                  <div key={team.teamId}>
                    <div
                      className="w-full"
                      style={{ height: CROSS_TEAM_TEAM_HEADER_HEIGHT }}
                      aria-hidden="true"
                    />
                    {!teamCollapsed && canViewPhases && (
                      <div
                        className="relative w-full"
                        style={{ height: CROSS_TEAM_PHASES_SPACER_HEIGHT }}
                      >
                        {phases.map((phase) => (
                          <PhaseItem
                            key={phase.id}
                            readOnly
                            phase={phase}
                            dateHeaderScale={dateHeaderScale}
                            timelineStart={timelineStart}
                            style={{
                              height: CROSS_TEAM_PHASE_HEIGHT,
                              top: CROSS_TEAM_PHASE_TOP,
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {visibleTracks.map((track) => {
                      const isSubtrack = track.parentTrackId !== null;
                      const rowHeight = isSubtrack
                        ? CROSS_TEAM_SUBTRACK_ROW_HEIGHT
                        : CROSS_TEAM_PARENT_ROW_HEIGHT;
                      const blockTop =
                        (rowHeight - CROSS_TEAM_BLOCK_HEIGHT) / 2;
                      return (
                        <div
                          key={track.id}
                          className="relative w-full"
                          style={{ height: rowHeight }}
                        >
                          {track.blocks.map((block) => (
                            <BlockItem
                              key={block.id}
                              readOnly
                              block={block}
                              teamId={teamId}
                              crossTeamId={team.teamId}
                              style={{
                                height: CROSS_TEAM_BLOCK_HEIGHT,
                                top: blockTop,
                              }}
                              dateHeaderScale={dateHeaderScale}
                              timelineStart={timelineStart}
                              selectedAthleteId={selectedAthleteId}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Today dot + vertical marker — hidden when blocks can't be
            displayed, since there are no rows to align them against. */}
        {canViewBlocks && (
          <>
            <div
              className="absolute top-7 translate-x-[-50%] bg-informational-700 rounded-full flex items-center justify-center w-6 h-6"
              style={{
                left: today.offset * dateHeaderScale,
              }}
            >
              <span className="text-sm text-white">{today.date}</span>
            </div>
            <div
              className="border-l absolute bottom-0 border-informational-700/20"
              style={{
                top: DATE_HEADER_HEIGHT,
                left: today.offset * dateHeaderScale,
              }}
            >
              {/* TODO: "Today" vertical marker */}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
